import { Spinner, useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';

import { InvoiceDetailView } from './InvoiceDetailView.js';
import { useInvoiceQuery } from './useInvoiceQuery.js';

/**
 * The `/invoices/$id` route component (promoted from a ComingSoon stub this
 * plan). Reads the `id` path param, fires the coarse detail query, and renders
 * the faithful `InvoiceDetailView` on success. Loading → accessible `Spinner`;
 * error → announced error state with a retry (the reports 3-state contract).
 *
 * The detail query does NOT set `staleTime: Infinity` (Pitfall 5) — the status
 * badge reflects the latest server value on each load.
 */

const routeApi = getRouteApi('/invoices/$id');

export function InvoiceDetailRoute() {
  const { t } = useTranslation();
  const { id } = routeApi.useParams();
  const invoiceId = Number(id);
  const query = useInvoiceQuery(invoiceId);

  if (query.isPending) {
    return (
      <div className="flex h-full items-center justify-center bg-background">
        <Spinner label={t('common:status.loading')} />
      </div>
    );
  }

  if (query.isError || !query.data.invoice) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('invoices:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('invoices:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('invoices:error.retry')}
        </button>
      </section>
    );
  }

  return <InvoiceDetailView invoice={query.data.invoice} />;
}
